import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdAdd, MdDelete, MdEdit, MdCheck, MdClose, MdToggleOn, MdToggleOff, MdPersonAddAlt1, MdPlayCircle, MdImage, MdVideocam,
  MdVisibility, MdOpenInNew, MdCalendarMonth,
} from 'react-icons/md';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';

/* ------------------------------------------------------------------ */
/* Phone-ratio preview: design + safe-area guide + fixed name/photo frame */
/* ------------------------------------------------------------------ */
function StatusPreview({ imageUrl, videoUrl, mediaType = 'image', width = 150 }) {
  const height = Math.round((width * 20) / 9); // 2160 x 4800 == 9:20
  const isVideo = mediaType === 'video';
  return (
    <div
      style={{
        position: 'relative', width, height, borderRadius: 12, overflow: 'hidden',
        background: '#111', flexShrink: 0,
      }}
    >
      {isVideo && videoUrl
        ? <video src={videoUrl} poster={imageUrl || undefined} controls muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : imageUrl
        ? <img src={imageUrl} alt="design" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 11 }}>No design</div>}

      {isVideo && (
        <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.55)', borderRadius: 8, padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 4 }}>
          <MdPlayCircle color="#fff" size={14} />
          <span style={{ color: '#fff', fontSize: 10, fontWeight: 600 }}>Video</span>
        </div>
      )}

      {!isVideo && (
        <>
          {/* Safe-area guide (~75% — keep art inside) */}
          <div style={{
            position: 'absolute', left: '5%', right: '5%', top: '8%', bottom: '22%',
            border: '1px dashed rgba(255,255,255,0.5)', borderRadius: 6, pointerEvents: 'none',
          }} />

          {/* Fixed name + photo frame (what the app draws) */}
          <div style={{
            position: 'absolute', left: 8, right: 8, bottom: 8,
            background: 'rgba(255,255,255,0.95)', borderRadius: 10, padding: '6px 8px',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <div style={{
              width: width * 0.16, height: width * 0.16, borderRadius: '50%',
              background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#9ca3af', flexShrink: 0,
            }}>
              <MdPersonAddAlt1 size={width * 0.09} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: Math.max(8, width * 0.06), fontWeight: 700, color: '#111' }}>Your Name</div>
              <div style={{ fontSize: Math.max(7, width * 0.045), color: '#dc2626', fontWeight: 600 }}>ఉదయం డిజిటల్</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Categories (tabs) manager                                           */
/* ------------------------------------------------------------------ */
function CategoriesCard({ categories, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', sort_order: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      await api.post('/admin/status/categories', {
        name: form.name, slug: form.slug || undefined, sort_order: Number(form.sort_order) || 0,
      });
      toast.success('Category added');
      setForm({ name: '', slug: '', sort_order: '' });
      setShowForm(false);
      reload();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const save = async (id) => {
    try {
      await api.put(`/admin/status/categories/${id}`, {
        name: editForm.name, slug: editForm.slug, sort_order: Number(editForm.sort_order) || 0,
      });
      toast.success('Updated'); setEditingId(null); reload();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const toggle = async (cat) => {
    try { await api.put(`/admin/status/categories/${cat.id}`, { is_active: !cat.is_active }); reload(); }
    catch { toast.error('Toggle failed'); }
  };

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/status/categories/${deleteModal.id}`);
      toast.success('Deleted');
      setDeleteModal(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="card">
      <div className="section-header">
        <h3>Category Tabs</h3>
        <button className="btn btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add</>}
        </button>
      </div>

      {showForm && (
        <form className="manage-form" onSubmit={create}>
          <div className="form-group mb-2">
            <input placeholder="Tab name (e.g. శుభోదయం)" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
          </div>
          <div className="form-group mb-2">
            <input placeholder="Slug (optional, e.g. good-morning)" value={form.slug}
              onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} />
          </div>
          <div className="form-group mb-2">
            <input type="number" placeholder="Sort order (lower = first)" value={form.sort_order}
              onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))} />
          </div>
          <button type="submit" className="btn btn-sm btn-primary">Add</button>
        </form>
      )}

      <div className="manage-list">
        {categories.map(cat => (
          <div key={cat.id} className="manage-item">
            {editingId === cat.id ? (
              <div className="manage-edit-row">
                <div className="form-group mb-2">
                  <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="form-group mb-2">
                  <input value={editForm.slug} onChange={e => setEditForm(p => ({ ...p, slug: e.target.value }))} />
                </div>
                <div className="form-group mb-2">
                  <input type="number" value={editForm.sort_order} onChange={e => setEditForm(p => ({ ...p, sort_order: e.target.value }))} />
                </div>
                <div className="flex-row gap-2 mt-2">
                  <button className="btn btn-sm btn-primary" onClick={() => save(cat.id)}><MdCheck /></button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setEditingId(null)}><MdClose /></button>
                </div>
              </div>
            ) : (
              <div className="manage-display-row">
                <div className="manage-item-info">
                  <span className="manage-name">{cat.name}</span>
                  <span className="manage-slug">{cat.slug} · #{cat.sort_order}</span>
                  {!cat.is_active && <span className="badge badge-expired ml-2">Hidden</span>}
                </div>
                <div className="action-btns">
                  <button className="btn btn-sm btn-ghost" onClick={() => toggle(cat)} title={cat.is_active ? 'Hide' : 'Show'}>
                    {cat.is_active ? <MdToggleOn className="text-green" /> : <MdToggleOff />}
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => { setEditingId(cat.id); setEditForm({ name: cat.name, slug: cat.slug, sort_order: cat.sort_order }); }}><MdEdit /></button>
                  <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(cat)}><MdDelete /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {categories.length === 0 && <p className="text-muted">No categories yet</p>}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Category</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this category?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.name}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Templates manager                                                   */
/* ------------------------------------------------------------------ */
const emptyForm = { title: '', category_id: '', media_type: 'image', image_url: '', video_url: '', thumbnail_url: '', tags: '', scheduled_date: '', sort_order: '', is_active: true };

function TemplatesCard({ categories }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const { page, limit, setPage, setLimit } = usePagination(30);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filterCat) params.category_id = filterCat;
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/admin/status/templates', { params });
      setTemplates(res.data.data);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: res.data.total || 0 });
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [filterCat, filterStatus, page, limit]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const uploadDesign = (isEdit = false) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
        // Upload original design to /status-image (high quality 95% mozjpeg)
        const main = await api.post('/admin/upload/status-image', fd, cfg);
        const mainUrl = main.data?.data?.url || main.data?.url;
        let thumbUrl = main.data?.data?.thumbnail_url || '';

        // If companion thumbnail wasn't returned, generate thumbnail via /status-thumbnail
        if (!thumbUrl) {
          try {
            const fd2 = new FormData(); fd2.append('file', file);
            const thumb = await api.post('/admin/upload/status-thumbnail', fd2, cfg);
            thumbUrl = thumb.data?.data?.url || thumb.data?.url || '';
          } catch { /* thumbnail optional */ }
        }

        const finalThumbUrl = thumbUrl || mainUrl;
        if (isEdit === true) {
          setEditingTemplate(p => p ? ({ ...p, image_url: mainUrl, thumbnail_url: finalThumbUrl }) : null);
          toast.success('Design updated');
        } else {
          setForm(p => ({ ...p, image_url: mainUrl, thumbnail_url: finalThumbUrl }));
          toast.success('Design uploaded');
        }
      } catch { toast.error('Upload failed'); }
      finally { setUploading(false); }
    };
    input.click();
  };

  const uploadVideoDesign = (isEdit = false) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
        const res = await api.post('/admin/upload/status-video', fd, cfg);
        const videoUrl = res.data?.data?.url || res.data?.url;
        const thumbUrl = res.data?.data?.thumbnail_url || '';
        if (isEdit === true) {
          setEditingTemplate(p => p ? ({ ...p, video_url: videoUrl, thumbnail_url: thumbUrl }) : null);
          toast.success('Video updated');
        } else {
          setForm(p => ({ ...p, video_url: videoUrl, thumbnail_url: thumbUrl }));
          toast.success('Video uploaded');
        }
      } catch { toast.error('Upload failed'); }
      finally { setUploading(false); }
    };
    input.click();
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.category_id) return toast.error('Pick a category');
    if (form.media_type === 'video') {
      if (!form.video_url) return toast.error('Upload a video');
    } else if (!form.image_url) {
      return toast.error('Upload a design');
    }
    try {
      await api.post('/admin/status/templates', {
        title: form.title || null,
        category_id: Number(form.category_id),
        media_type: form.media_type,
        image_url: form.image_url || null,
        video_url: form.media_type === 'video' ? form.video_url : null,
        thumbnail_url: form.thumbnail_url || null,
        tags: form.tags || null,
        scheduled_date: form.scheduled_date || null,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
      });
      toast.success('Template added');
      setForm(emptyForm);
      setShowForm(false);
      setPage(1);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingTemplate) return;
    if (editingTemplate.media_type === 'video') {
      if (!editingTemplate.video_url) return toast.error('Upload a video');
    } else if (!editingTemplate.image_url) {
      return toast.error('Upload a design');
    }
    setSavingEdit(true);
    try {
      await api.put(`/admin/status/templates/${editingTemplate.id}`, {
        title: editingTemplate.title || null,
        category_id: Number(editingTemplate.category_id),
        media_type: editingTemplate.media_type,
        image_url: editingTemplate.image_url || null,
        video_url: editingTemplate.media_type === 'video' ? editingTemplate.video_url : null,
        thumbnail_url: editingTemplate.thumbnail_url || null,
        tags: editingTemplate.tags || null,
        scheduled_date: editingTemplate.scheduled_date || null,
        sort_order: Number(editingTemplate.sort_order) || 0,
        is_active: editingTemplate.is_active,
      });
      toast.success('Status template updated successfully');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update template');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggle = async (t) => {
    try { await api.put(`/admin/status/templates/${t.id}`, { is_active: !t.is_active }); fetchTemplates(); }
    catch { toast.error('Toggle failed'); }
  };

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/status/templates/${deleteModal.id}`);
      toast.success('Status template deleted successfully.');
      setDeleteModal(null);
      fetchTemplates();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="card">
      <div className="section-header">
        <h3>Status Templates</h3>
        <div className="flex-row items-center gap-2">
          <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Live">Live</option>
            <option value="Expired">Expired</option>
          </select>
          <select className="filter-select" value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn btn-sm btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add Template</>}
          </button>
        </div>
      </div>

      {showForm && (
        <form className="card form-card" onSubmit={create} style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {/* Left: live preview */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div className="flex-row gap-2">
              <button
                type="button"
                className={`btn btn-sm ${form.media_type === 'image' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => set('media_type', 'image')}
              >
                <MdImage /> Image
              </button>
              <button
                type="button"
                className={`btn btn-sm ${form.media_type === 'video' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => set('media_type', 'video')}
              >
                <MdVideocam /> Video
              </button>
            </div>
            <StatusPreview imageUrl={form.image_url} videoUrl={form.video_url} mediaType={form.media_type} width={170} />
            {form.media_type === 'video' ? (
              <>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => uploadVideoDesign(false)} disabled={uploading}>
                  {uploading ? 'Uploading…' : (form.video_url ? 'Replace video' : 'Upload video')}
                </button>
                <span className="text-muted" style={{ fontSize: 11, textAlign: 'center', maxWidth: 180 }}>
                  A poster thumbnail is auto-extracted from the first frame.
                </span>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => uploadDesign(false)} disabled={uploading}>
                  {uploading ? 'Uploading…' : (form.image_url ? 'Replace design' : 'Upload design')}
                </button>
                <span className="text-muted" style={{ fontSize: 11, textAlign: 'center', maxWidth: 180 }}>
                  2160 × 4800 px. Keep art inside the dashed safe area; the name/photo card is added automatically.
                </span>
              </>
            )}
          </div>

          {/* Right: fields */}
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="form-group">
              <label>Category *</label>
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)} required>
                <option value="">Select…</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Title (optional, internal)</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Shivaratri 2026" />
            </div>
            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="festival, trending" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Schedule date (for “Today’s special”)</label>
                <input type="datetime-local" value={form.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Sort order</label>
                <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} placeholder="0" />
              </div>
            </div>
            <label className="checkbox-label">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              Active (visible in app)
            </label>
            <button type="submit" className="btn btn-primary mt-md" disabled={uploading}>Add Template</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-muted">Loading…</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16, marginTop: 12 }}>
          {templates.map(t => {
            let statusBadge = null;
            if (t.status === 'Scheduled') statusBadge = <span className="badge" style={{background:'#FEF08A', color:'#854D0E'}}>🟡 Scheduled</span>;
            else if (t.status === 'Live') statusBadge = <span className="badge" style={{background:'#BBF7D0', color:'#166534'}}>🟢 Live</span>;
            else if (t.status === 'Draft') statusBadge = <span className="badge" style={{background: 'var(--bg)', color: 'var(--text)'}}>⚪ Draft</span>;
            else if (t.status === 'Expired') statusBadge = <span className="badge badge-expired">🔴 Expired</span>;

            return (
            <div key={t.id} className="card" style={{ padding: 10, display: 'flex', flexDirection: 'column' }}>
              <div
                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                onClick={() => setViewTemplate(t)}
                title="Click to view full status design"
              >
                <StatusPreview imageUrl={t.image_url || t.thumbnail_url} videoUrl={t.video_url} mediaType={t.media_type} width={130} />
              </div>
              <div style={{ marginTop: 8, flex: 1 }}>
                <span className="badge">{t.category?.name || '—'}</span>
                {statusBadge && <span className="ml-2">{statusBadge}</span>}
                {t.scheduled_date && (
                  <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                    📅 {new Date(t.scheduled_date).toLocaleDateString()}
                    <br />
                    🕒 {new Date(t.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
                {t.title && <div style={{ fontSize: 12, marginTop: 2, fontWeight: 600 }}>{t.title}</div>}
                <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{t.share_count || 0} shares</div>
              </div>
              <div className="action-btns" style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid var(--border)', display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => setViewTemplate(t)}
                  title="View Details & Original High-Quality Image"
                  style={{ padding: '4px 6px' }}
                >
                  <MdVisibility size={15} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => setEditingTemplate({
                    ...t,
                    scheduled_date: t.scheduled_date ? new Date(new Date(t.scheduled_date).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''
                  })}
                  title="Edit Template"
                  style={{ padding: '4px 6px' }}
                >
                  <MdEdit size={15} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => toggle(t)}
                  title={t.is_active ? 'Hide' : 'Show'}
                  style={{ padding: '4px 6px' }}
                >
                  {t.is_active ? <MdToggleOn className="text-green" size={20} /> : <MdToggleOff size={20} />}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => setDeleteModal(t)}
                  title="Delete Template"
                  style={{ padding: '4px 6px' }}
                >
                  <MdDelete size={15} />
                </button>
              </div>
            </div>
          )})}
          {templates.length === 0 && <p className="text-muted">No templates yet</p>}
        </div>
      )}

      {/* View Template Modal (Original High-Quality Display) */}
      {viewTemplate && (
        <div className="modal-overlay" onClick={() => setViewTemplate(null)}>
          <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ margin: 0 }}>Status Design Details</h3>
                <span className="badge">
                  {viewTemplate.status || (viewTemplate.is_active ? 'Live' : 'Draft')}
                </span>
              </div>
              <button className="modal-close" onClick={() => setViewTemplate(null)}><MdClose /></button>
            </div>

            <div style={{ padding: 24, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {/* Left: Full High-Quality Design Preview */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <StatusPreview
                  imageUrl={viewTemplate.image_url}
                  videoUrl={viewTemplate.video_url}
                  mediaType={viewTemplate.media_type}
                  width={180}
                />
                {viewTemplate.image_url && (
                  <a
                    href={viewTemplate.image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-ghost"
                    style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}
                  >
                    <MdOpenInNew size={14} /> Full Image (Original Quality)
                  </a>
                )}
              </div>

              {/* Right: Info Details */}
              <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                    Title
                  </label>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>
                    {viewTemplate.title || <span className="text-muted" style={{ fontStyle: 'italic', fontWeight: 400 }}>No title provided</span>}
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                    Category
                  </label>
                  <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>
                    {viewTemplate.category?.name || '—'}
                  </div>
                </div>

                {viewTemplate.scheduled_date && (
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      Scheduled Date
                    </label>
                    <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MdCalendarMonth size={16} style={{ color: '#2563eb' }} />
                      {new Date(viewTemplate.scheduled_date).toLocaleString()}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      Active in App
                    </label>
                    <div style={{ fontSize: 13, marginTop: 2, fontWeight: 600 }}>
                      {viewTemplate.is_active ? '🟢 Active' : '⚪ Inactive'}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      Sort Order
                    </label>
                    <div style={{ fontSize: 13, marginTop: 2, fontWeight: 600 }}>
                      {viewTemplate.sort_order ?? 0}
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                    Shares
                  </label>
                  <div style={{ fontSize: 13, marginTop: 2 }}>
                    ↗ {viewTemplate.share_count || 0} times shared
                  </div>
                </div>

                {viewTemplate.tags && (
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      Tags
                    </label>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {viewTemplate.tags}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => {
                  const t = viewTemplate;
                  setViewTemplate(null);
                  setEditingTemplate({
                    ...t,
                    scheduled_date: t.scheduled_date ? new Date(new Date(t.scheduled_date).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''
                  });
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <MdEdit size={16} /> Edit This Template
              </button>

              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setViewTemplate(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {editingTemplate && (
        <div className="modal-overlay" onClick={() => setEditingTemplate(null)}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Status Template</h3>
              <button className="modal-close" onClick={() => setEditingTemplate(null)}><MdClose /></button>
            </div>
            <form onSubmit={saveEdit} style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: '20px 24px' }}>
              {/* Left: preview & replace */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <StatusPreview
                  imageUrl={editingTemplate.image_url}
                  videoUrl={editingTemplate.video_url}
                  mediaType={editingTemplate.media_type}
                  width={150}
                />
                {editingTemplate.media_type === 'video' ? (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => uploadVideoDesign(true)} disabled={uploading}>
                    {uploading ? 'Uploading…' : 'Replace video'}
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => uploadDesign(true)} disabled={uploading}>
                      {uploading ? 'Uploading…' : 'Replace design'}
                    </button>
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#1e40af', background: '#dbeafe', padding: '2px 8px', borderRadius: 6 }}>
                      High Quality 9:20
                    </span>
                  </>
                )}
              </div>

              {/* Right: fields */}
              <div style={{ flex: 1, minWidth: 240 }}>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={editingTemplate.category_id || ''}
                    onChange={e => setEditingTemplate(p => ({ ...p, category_id: e.target.value }))}
                    required
                  >
                    <option value="">Select…</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Title (optional, internal)</label>
                  <input
                    value={editingTemplate.title || ''}
                    onChange={e => setEditingTemplate(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Shivaratri 2026"
                  />
                </div>
                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input
                    value={editingTemplate.tags || ''}
                    onChange={e => setEditingTemplate(p => ({ ...p, tags: e.target.value }))}
                    placeholder="festival, trending"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Schedule date</label>
                    <input
                      type="datetime-local"
                      value={editingTemplate.scheduled_date || ''}
                      onChange={e => setEditingTemplate(p => ({ ...p, scheduled_date: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Sort order</label>
                    <input
                      type="number"
                      value={editingTemplate.sort_order ?? 0}
                      onChange={e => setEditingTemplate(p => ({ ...p, sort_order: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                </div>
                <label className="checkbox-label" style={{ marginTop: 10 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(editingTemplate.is_active)}
                    onChange={e => setEditingTemplate(p => ({ ...p, is_active: e.target.checked }))}
                  />
                  Active (visible in app)
                </label>
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-ghost" onClick={() => setEditingTemplate(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={uploading || savingEdit}>
                    {savingEdit ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {!loading && templates.length > 0 && (
        <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Status Template</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this status template?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title || 'Untitled Template'}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
export default function StatusManager() {
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try { const res = await api.get('/admin/status/categories'); setCategories(res.data.data); }
    catch { toast.error('Failed to load categories'); }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>Status Maker</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 340px) 1fr', gap: 20, alignItems: 'start' }} className="status-grid">
        <CategoriesCard categories={categories} reload={loadCategories} />
        <TemplatesCard categories={categories} />
      </div>
    </div>
  );
}
